import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useDesignGalleryFiltersContext } from '../../DesignGalleryFiltersProvider';
import type { FacetClickSource } from './Filter.types';
import { deriveFormatDisplayParam, isFormatFacetOption } from './formatDisplayParam';
import { FORMAT_SEARCH_PARAM_KEY } from './useFormatSearchParam';

export type ToggleFilterSource = FacetClickSource | 'activeFilterChip';

/**
 * The one place a filter option is switched on or off: logs telemetry, updates `pf`, and
 * keeps the `?format=` tile-price display param in step with the Format group. Used by the
 * pill / drawer checkboxes and by the applied-filter chips.
 */
export const useToggleFilter = () => {
  const { buttonInteracted } = useCardTelemetry();
  const { setActiveFilter } = useDesignGalleryFiltersContext();
  const updateSearchParam = useUpdateSearchParam();

  return useEventCallback((compositeCode: string, checked: boolean, context: Readonly<{ source: ToggleFilterSource; facet?: string }>) => {
    const facet = context.facet ?? compositeCode.split(':')[0];
    buttonInteracted({ name: 'designsGallery_filterFacetClicked', facet, source: context.source, code: compositeCode, isChecked: checked });
    // The selection `setActiveFilter` actually pushed, rather than this render's snapshot of
    // it: two toggles in one frame would otherwise derive the display param from a stale set,
    // and the param would stay wrong until the next toggle (joy-crit).
    const next = setActiveFilter(compositeCode, checked);

    if (isFormatFacetOption(compositeCode)) {
      // `setActiveFilter` has already pushed the new `pf`; replace in place so the display
      // param rides the same history entry.
      updateSearchParam(FORMAT_SEARCH_PARAM_KEY, deriveFormatDisplayParam(next), 'replace');
    }
  });
};
